import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { useBookingCreate } from '../booking/BookingCreateContext';
import { useAuth } from '../auth/AuthContext';
import { useFavoriteAssets } from '../favorites/FavoriteAssetsContext';
import {
  assistantSearchIntentFromTurn,
  postAssistantTurn,
  shouldPreferLocalAssistantSearch,
} from '../api/assistantTurn';
import {
  parseAssistantIntent,
  stripAssistantCorrectionLead,
  type AssistantIntent,
  type AssistantSearchIntent,
} from './parseAssistantIntent';
import {
  executeAssistantIntent,
  executeAssistantTurn,
  type AssistantExecutionResult,
} from './executeAssistantIntent';
import {
  isClearChatUtterance,
  isResetSearchUtterance,
  mergeAssistantSearchIntents,
  resolveAssistantSearchIntent,
  shouldMergeAssistantSearch,
} from './mergeAssistantSearch';
import { ISRAEL_COUNTRY_ID } from '../utils/assetCountryCurrency';
import { suggestVoiceSearchCities, pickConfidentVoiceCitySuggestion } from './suggestVoiceSearchCities';
import { preferInclusiveAssistantPrice } from './assistantPriceConstraint';
import { assistantPriceSortReply } from './assistantPriceSortReply';
import { assistantProximityReply } from './assistantProximityReply';
import { formatAssistantFoundCount, withAssistantFoundCount } from './assistantFoundCount';
import { formatAssistantSearchAction } from './assistantVoiceLines';
import { markAssistantClearedForGreeting, markAssistantVoiceActivity } from './assistantVoiceSession';
import {
  beginAssistantSearchHitsSession,
  clearAssistantSearchHits,
  commitAssistantSearchHits,
  getAssistantSearchHits,
  isAssistantSearchHitsEmpty,
  isAssistantSearchHitsReady,
  subscribeAssistantSearchHits,
} from './assistantSearchHits';
import { geocodeAddressDetails } from '../utils/geocodeAddress';
import type { MessageKey } from '../i18n/messages';

export type AssistantChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  /** Previous-session bubbles kept on screen but visually muted after New search. */
  archived?: boolean;
};

export type PendingBookConfirm = {
  assetQuery: string | null;
};

let assistantMessageSeq = 0;

function nextAssistantMessageId(role: AssistantChatMessage['role']): string {
  assistantMessageSeq += 1;
  return `${role[0]}-${assistantMessageSeq}`;
}

function appendAssistantReply(
  prev: AssistantChatMessage[],
  text: string,
): AssistantChatMessage[] {
  const last = prev[prev.length - 1];
  if (last?.role === 'assistant' && last.text === text && !last.archived) {
    return prev;
  }
  return [...prev, { id: nextAssistantMessageId('assistant'), role: 'assistant', text }];
}

function archiveChatMessages(prev: AssistantChatMessage[]): AssistantChatMessage[] {
  if (prev.length === 0 || prev.every((message) => message.archived)) {
    return prev;
  }
  return prev.map((message) => (message.archived ? message : { ...message, archived: true }));
}

function resultToText(
  result: AssistantExecutionResult,
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
): string {
  if ('summaryText' in result && result.summaryText) {
    return result.summaryText;
  }
  if (result.ok === false) {
    return t(result.errorKey, result.errorParams);
  }
  if ('summaryKey' in result && result.summaryKey) {
    return t(result.summaryKey, 'summaryParams' in result ? result.summaryParams : undefined);
  }
  return t('assistant.error.unknown');
}

export function useAssistantController() {
  const { t, language } = useI18n();
  const nav = useAppNavigation();
  const { isLoggedIn } = useAuth();
  const { clearAllFavorites, favorites } = useFavoriteAssets();
  const { requestAssetQuickBook } = useBookingCreate();
  const [messages, setMessages] = useState<AssistantChatMessage[]>([]);
  const [thinking, setThinking] = useState(false);
  const [pendingBook, setPendingBook] = useState<PendingBookConfirm | null>(null);
  const [lastSearch, setLastSearch] = useState<AssistantSearchIntent | null>(null);
  const [lastAssetSnapshot, setLastAssetSnapshot] = useState<{
    id: string;
    record: Record<string, unknown>;
  } | null>(null);
  const pendingClarifyRef = useRef<Extract<AssistantIntent, { kind: 'clarifyPlace' }> | null>(
    null,
  );

  const navApi = useMemo(
    () => ({
      view: nav.view,
      openSearch: nav.openSearch,
      updateSearchQuery: nav.updateSearchQuery,
      openFavorites: nav.openFavorites,
      openMessages: nav.openMessages,
      openProfile: nav.openProfile,
      openAssets: (params?: Parameters<typeof nav.openAssets>[0]) =>
        nav.openAssets(params, { keepAssistantHits: true }),
      openBookings: nav.openBookings,
      openAuth: nav.openAuth,
      navigateFromBottomBar: nav.navigateFromBottomBar,
      setView: nav.setView,
    }),
    [nav],
  );

  const lastSearchRef = useRef<AssistantSearchIntent | null>(null);
  lastSearchRef.current = lastSearch;
  const turnSeqRef = useRef(0);
  const pendingSearchSeqRef = useRef<number | null>(null);
  const pendingSearchResultRef = useRef<AssistantExecutionResult | null>(null);
  const thinkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mergeSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAssistantTimers = useCallback(() => {
    if (thinkingTimerRef.current != null) {
      clearTimeout(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
    if (mergeSettleTimerRef.current != null) {
      clearTimeout(mergeSettleTimerRef.current);
      mergeSettleTimerRef.current = null;
    }
  }, []);

  const stopThinking = useCallback(() => {
    clearAssistantTimers();
    setThinking(false);
  }, [clearAssistantTimers]);

  const settleSearchHits = useCallback(
    (seq: number, rows: Record<string, unknown>[], timedOut = false) => {
      if (seq !== turnSeqRef.current || pendingSearchSeqRef.current !== seq) {
        return;
      }
      if (pendingSearchResultRef.current == null && !timedOut) {
        return;
      }
      const result = pendingSearchResultRef.current;
      pendingSearchSeqRef.current = null;
      pendingSearchResultRef.current = null;

      const finish = (text: string | null) => {
        if (seq !== turnSeqRef.current) {
          return;
        }
        stopThinking();
        if (text) {
          setMessages((prev) => appendAssistantReply(prev, text));
        }
      };

      if (rows.length === 0 && (!timedOut || isAssistantSearchHitsEmpty())) {
        finish(t('assistant.result.noMatch'));
        return;
      }
      if (!result) {
        finish(null);
        return;
      }

      if (result.ok && 'proximityStreet' in result && result.proximityStreet && rows.length > 0) {
        const street = result.proximityStreet;
        const streetLabel = result.proximityStreetDisplay || street;
        const cityRaw = result.proximityCity?.trim() || lastSearchRef.current?.city?.trim() || '';
        const city = cityRaw.split(/\s*[-–]\s*/)[0]?.trim() || cityRaw;
        const query = city ? `${street}, ${city}` : street;
        void geocodeAddressDetails(query, {
          settlementHe: city || null,
          streetLine: street,
        })
          .then((anchor) => {
            if (seq !== turnSeqRef.current) {
              return;
            }
            if (!anchor) {
              finish(t('assistant.result.proximityNoGeo'));
              return;
            }
            const { text, sorted } = assistantProximityReply(rows, anchor, streetLabel, t, language);
            commitAssistantSearchHits(sorted);
            const previous = lastSearchRef.current;
            navApi.openAssets({
              catalogBrowse: true,
              assetTypeLabel: previous?.assetTypeLabel ?? null,
              city: previous?.city ?? (city || null),
              officeFamily: Boolean(previous?.officeFamily),
              open24h: Boolean(previous?.open24h),
              price: previous?.price ?? null,
              listSort: 'distance',
              proximityAnchor: { lat: anchor.lat, lng: anchor.lng },
              street: null,
              listSearch: null,
            });
            finish(withAssistantFoundCount(text, sorted.length, t));
          })
          .catch(() => {
            finish(t('assistant.result.proximityNoGeo'));
          });
        return;
      }

      // Action line was already shown when the search started; settle with count
      // (plus price/proximity narrative when those apply).
      if (result.ok && 'priceSort' in result && result.priceSort && rows.length > 0) {
        finish(
          withAssistantFoundCount(
            assistantPriceSortReply(rows, result.priceSort, t, language),
            rows.length,
            t,
          ),
        );
        return;
      }
      finish(formatAssistantFoundCount(rows.length, t));
    },
    [stopThinking, t, language, navApi],
  );

  const rememberAssetForBooking = useCallback((id: string, record: Record<string, unknown>) => {
    setLastAssetSnapshot({ id, record });
  }, []);

  const resetSearch = useCallback(() => {
    turnSeqRef.current += 1;
    pendingSearchSeqRef.current = null;
    pendingSearchResultRef.current = null;
    stopThinking();
    setLastSearch(null);
    lastSearchRef.current = null;
    setPendingBook(null);
    pendingClarifyRef.current = null;
    clearAssistantSearchHits();
    setMessages(archiveChatMessages);
    markAssistantClearedForGreeting();
  }, [stopThinking]);

  const clearMessages = useCallback(() => {
    turnSeqRef.current += 1;
    pendingSearchSeqRef.current = null;
    pendingSearchResultRef.current = null;
    stopThinking();
    setMessages([]);
    setPendingBook(null);
    setLastSearch(null);
    lastSearchRef.current = null;
    pendingClarifyRef.current = null;
    clearAssistantSearchHits();
    markAssistantClearedForGreeting();
  }, [stopThinking]);

  useEffect(() => {
    return subscribeAssistantSearchHits((rows) => {
      const seq = pendingSearchSeqRef.current;
      if (seq == null || pendingSearchResultRef.current == null) {
        return;
      }
      settleSearchHits(seq, rows);
    });
  }, [settleSearchHits]);

  useEffect(() => () => clearAssistantTimers(), [clearAssistantTimers]);

  const runTextCommand = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) {
        return;
      }
      if (isClearChatUtterance(trimmed)) {
        clearMessages();
        return;
      }
      if (isResetSearchUtterance(trimmed)) {
        resetSearch();
        return;
      }
      setMessages((prev) => [
        ...prev,
        { id: nextAssistantMessageId('user'), role: 'user', text: trimmed },
      ]);

      const seq = (turnSeqRef.current += 1);
      const previousSearch = lastSearchRef.current;
      const startFresh = isAssistantSearchHitsEmpty();
      const correctionBody = stripAssistantCorrectionLead(trimmed);
      const textToParse = correctionBody ?? trimmed;
      let localIntent: AssistantIntent = parseAssistantIntent(textToParse, {
        fallbackCity: startFresh ? null : previousSearch?.city,
      });

      // Resume after “Did you mean Ramat Gan?” — user answers with a city name.
      const pendingClarify = pendingClarifyRef.current;
      if (pendingClarify && localIntent.kind === 'search' && localIntent.city) {
        localIntent = {
          ...pendingClarify.pendingSearch,
          city: localIntent.city,
          countryId: localIntent.countryId ?? ISRAEL_COUNTRY_ID,
          street: localIntent.street,
          streetDisplay: localIntent.streetDisplay,
          venue: null,
          keyword: localIntent.keyword || pendingClarify.pendingSearch.keyword,
          assetTypeLabel:
            localIntent.assetTypeLabel || pendingClarify.pendingSearch.assetTypeLabel,
          officeFamily: Boolean(
            localIntent.officeFamily || pendingClarify.pendingSearch.officeFamily,
          ),
          raw: trimmed,
        };
        pendingClarifyRef.current = null;
      } else if (pendingClarify && localIntent.kind === 'search' && !localIntent.city) {
        const guess = pickConfidentVoiceCitySuggestion(
          suggestVoiceSearchCities(localIntent.venue || textToParse, { limit: 3 }),
        );
        if (guess) {
          localIntent = {
            ...pendingClarify.pendingSearch,
            city: guess.cityHe,
            countryId: ISRAEL_COUNTRY_ID,
            venue: null,
            raw: trimmed,
          };
          pendingClarifyRef.current = null;
        }
      }

      // “I meant Ramat Gan” after a failed office search — keep the prior type filters.
      const forceMergeCorrection = Boolean(
        correctionBody && previousSearch && localIntent.kind === 'search',
      );
      if (forceMergeCorrection && previousSearch && localIntent.kind === 'search') {
        localIntent = {
          ...mergeAssistantSearchIntents(previousSearch, localIntent),
          // Correction replaces a bad place leftover (“ramadan”), never keeps it.
          venue: null,
        };
      }

      const willMerge =
        forceMergeCorrection ||
        (localIntent.kind === 'search' &&
          shouldMergeAssistantSearch(previousSearch, localIntent, trimmed, navApi.view));
      const mergeFrom = willMerge ? previousSearch : null;
      const waitingForHits = localIntent.kind === 'search';
      pendingSearchSeqRef.current = waitingForHits ? seq : null;
      pendingSearchResultRef.current = null;
      if (localIntent.kind === 'clarifyPlace') {
        pendingClarifyRef.current = localIntent;
      } else if (localIntent.kind === 'search') {
        pendingClarifyRef.current = null;
      }
      clearAssistantTimers();
      setThinking(true);
      thinkingTimerRef.current = setTimeout(() => {
        thinkingTimerRef.current = null;
        if (waitingForHits) {
          settleSearchHits(seq, getAssistantSearchHits(), true);
        } else if (seq === turnSeqRef.current) {
          setThinking(false);
        }
      }, 12000);

      const applyResult = (result: AssistantExecutionResult) => {
        if (seq !== turnSeqRef.current) {
          return;
        }
        if (result.ok && 'pendingBookConfirm' in result && result.pendingBookConfirm) {
          pendingSearchSeqRef.current = null;
          pendingSearchResultRef.current = null;
          stopThinking();
          setPendingBook({ assetQuery: result.assetQuery });
          const bookText =
            'summaryText' in result && result.summaryText
              ? result.summaryText
              : result.summaryKey
                ? t(result.summaryKey, {
                    q: result.assetQuery ?? t('assistant.book.currentAsset'),
                  })
                : t('assistant.result.bookConfirmPrompt', {
                    q: result.assetQuery ?? t('assistant.book.currentAsset'),
                  });
          setMessages((prev) => appendAssistantReply(prev, bookText));
          return;
        }
        setPendingBook(null);
        if (result.ok && pendingSearchSeqRef.current === seq) {
          pendingSearchResultRef.current = result;
          const action = formatAssistantSearchAction(result, language, t);
          if (action) {
            setMessages((prev) => appendAssistantReply(prev, action));
          }
          markAssistantVoiceActivity();
          // Amenity / open-weekday hard-filters re-hydrate from detail; do not settle on
          // the previous hit list (partial snapshots → empty / wrong count).
          const waitForDetailHydrate =
            localIntent.kind === 'search' &&
            (Boolean(localIntent.amenityIcons?.length) ||
              Boolean(
                (localIntent.schedule?.weekdays?.length ||
                  localIntent.schedule?.closed_weekdays?.length) &&
                  !localIntent.schedule?.start_time,
              ));
          if (
            isAssistantSearchHitsReady() &&
            mergeSettleTimerRef.current == null &&
            !waitForDetailHydrate
          ) {
            mergeSettleTimerRef.current = setTimeout(() => {
              mergeSettleTimerRef.current = null;
              if (pendingSearchSeqRef.current !== seq) {
                return;
              }
              settleSearchHits(seq, getAssistantSearchHits());
            }, 450);
          }
          return;
        }
        pendingSearchSeqRef.current = null;
        pendingSearchResultRef.current = null;
        stopThinking();
        setMessages((prev) => appendAssistantReply(prev, resultToText(result, t)));
        markAssistantVoiceActivity();
      };

      const runLocalParser = () => {
        if (seq !== turnSeqRef.current) {
          return;
        }
        const parsed = localIntent;
        if (parsed.kind === 'search') {
          const nextSearch = resolveAssistantSearchIntent(parsed, mergeFrom, navApi.view).intent;
          if (nextSearch.favoritesOnly && favorites.length > 0) {
            nextSearch.assetIds = favorites.map((entry) => entry.id).filter(Boolean);
            nextSearch.venue = null;
          }
          lastSearchRef.current = nextSearch;
          setLastSearch(nextSearch);
        }
        applyResult(
          executeAssistantIntent(parsed, {
            nav: navApi,
            t,
            language,
            previousSearch: mergeFrom,
            deferBookConfirm: true,
            clearAllFavorites,
            favoriteAssets: favorites,
          }),
        );
      };

      if (willMerge) {
        runLocalParser();
        return;
      }

      // Navigate / book / FAQ / clear-favorites — never treat as empty catalog search.
      if (localIntent.kind !== 'search') {
        runLocalParser();
        return;
      }

      clearAssistantSearchHits();
      beginAssistantSearchHitsSession();

      void (async () => {
        const turn = await postAssistantTurn({
          utterance: trimmed,
          language,
          currentScreen: navApi.view,
          lastSearch: null,
        });
        if (seq !== turnSeqRef.current) {
          return;
        }
        if (!turn) {
          runLocalParser();
          return;
        }
        if (shouldPreferLocalAssistantSearch(turn, localIntent)) {
          runLocalParser();
          return;
        }
        if (turn.action === 'search' || turn.action === 'refine') {
          const fromTurn = assistantSearchIntentFromTurn(turn, trimmed);
          if (!fromTurn) {
            runLocalParser();
            return;
          }
          const resolved = resolveAssistantSearchIntent(
            localIntent.kind === 'search' ? localIntent : fromTurn,
            mergeFrom,
            navApi.view,
          );
          if (fromTurn.assetIds?.length && !resolved.intent.assetIds?.length) {
            resolved.intent.assetIds = fromTurn.assetIds;
          }
          if (localIntent.kind === 'search') {
            resolved.intent.price = preferInclusiveAssistantPrice(
              resolved.intent.price,
              localIntent.price,
            );
            resolved.intent.priceSort = localIntent.priceSort ?? resolved.intent.priceSort ?? null;
          }
          lastSearchRef.current = resolved.intent;
          setLastSearch(resolved.intent);
          if (mergeFrom) {
            applyResult(
              executeAssistantIntent(localIntent.kind === 'search' ? localIntent : fromTurn, {
                nav: navApi,
                t,
                language,
                previousSearch: mergeFrom,
                deferBookConfirm: true,
              }),
            );
          } else {
            applyResult(
              executeAssistantTurn(turn, {
                nav: navApi,
                language,
                priceOverlay: localIntent.kind === 'search' ? localIntent.price : null,
                priceSortOverlay: localIntent.kind === 'search' ? localIntent.priceSort ?? null : null,
                scheduleOverlay: localIntent.kind === 'search' ? localIntent.schedule ?? null : null,
                amenityIconsOverlay:
                  localIntent.kind === 'search' ? localIntent.amenityIcons ?? null : null,
              }),
            );
          }
          return;
        }
        applyResult(
          executeAssistantTurn(turn, {
            nav: navApi,
            previousSearch: mergeFrom,
            language,
            scheduleOverlay: localIntent.kind === 'search' ? localIntent.schedule ?? null : null,
          }),
        );
      })();
    },
    [
      navApi,
      t,
      language,
      resetSearch,
      clearMessages,
      clearAssistantTimers,
      settleSearchHits,
      stopThinking,
      clearAllFavorites,
      favorites,
    ],
  );

  const confirmPendingBook = useCallback(() => {
    if (!pendingBook) {
      return;
    }
    if (!isLoggedIn) {
      setMessages((prev) => appendAssistantReply(prev, t('assistant.error.bookNeedsLogin')));
      navApi.openAuth('login');
      setPendingBook(null);
      return;
    }

    const query = pendingBook.assetQuery?.trim();
    if (query) {
      navApi.openSearch(query);
      setMessages((prev) =>
        appendAssistantReply(prev, t('assistant.result.bookSearchFirst', { q: query })),
      );
      setPendingBook(null);
      return;
    }

    if (lastAssetSnapshot) {
      requestAssetQuickBook(lastAssetSnapshot.id, lastAssetSnapshot.record);
      navApi.openBookings({ preserveCreateFlow: true });
      setMessages((prev) => appendAssistantReply(prev, t('assistant.result.bookOpened')));
    } else {
      setMessages((prev) => appendAssistantReply(prev, t('assistant.error.bookNoAsset')));
    }
    setPendingBook(null);
  }, [pendingBook, isLoggedIn, lastAssetSnapshot, requestAssetQuickBook, navApi, t]);

  const cancelPendingBook = useCallback(() => {
    setPendingBook(null);
    setMessages((prev) => appendAssistantReply(prev, t('assistant.result.bookCancelled')));
  }, [t]);

  return {
    messages,
    thinking,
    pendingBook,
    runTextCommand,
    confirmPendingBook,
    cancelPendingBook,
    rememberAssetForBooking,
    resetSearch,
    clearMessages,
  };
}
